// Main-thread wrapper around mattingWorker.ts. Mirrors the warm/run shape of
// the face-parser client. `runMatting` returns the person-alpha plane at model
// resolution; the recolour layer (hairAlphaMatte.ts) upscales + intersects it
// with the hair-region mask.

export interface MattingResult {
  alpha: Float32Array; // length aw*ah, values 0..1
  aw: number;
  ah: number;
}

const REQUEST_TIMEOUT_MS = 30_000;

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<
  number,
  { resolve: (r: MattingResult) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }
>();

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./mattingWorker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (ev: MessageEvent<any>) => {
    const msg = ev.data;
    if (msg.type === 'result' || msg.type === 'error') {
      const entry = pending.get(msg.requestId);
      if (!entry) return;
      clearTimeout(entry.timer);
      pending.delete(msg.requestId);
      if (msg.type === 'result') entry.resolve({ alpha: msg.alpha as Float32Array, aw: msg.aw, ah: msg.ah });
      else entry.reject(new Error(msg.reason ?? 'matting_failed'));
    }
  };
  worker.onerror = () => {
    for (const [, entry] of pending) {
      clearTimeout(entry.timer);
      entry.reject(new Error('matting_worker_error'));
    }
    pending.clear();
  };
  return worker;
}

// Idempotent — kicks off model download/compile so the first real call is fast.
export function warmMattingModel(): void {
  ensureWorker().postMessage({ type: 'warm' });
}

// Termina il worker e libera runtime ORT + wasm + modello residenti. L'alpha
// viene cachata per-snapshot dal chiamante, quindi dopo il run il worker è solo
// peso morto in RAM — su un device 4GB (iPhone 11 Pro) è esattamente il margine
// che manca al pass capelli prima del jetsam. Simmetrico a destroyOnnxBridge;
// ensureWorker() ricrea tutto in lazy al prossimo runMatting.
export function destroyMattingWorker(): void {
  if (!worker) return;
  for (const [, entry] of pending) {
    clearTimeout(entry.timer);
    entry.reject(new Error('matting_worker_destroyed'));
  }
  pending.clear();
  worker.terminate();
  worker = null;
}

// Runs MODNet on a copy of `source` (the original bitmap is left intact for the
// render pipeline). Rejects on timeout or worker/session failure — callers fall
// back to the heuristic matte.
export async function runMatting(source: ImageBitmap): Promise<MattingResult> {
  const w = ensureWorker();
  // Copy so transferring to the worker doesn't neuter the caller's bitmap.
  const copy = await createImageBitmap(source);
  const requestId = nextId++;
  return new Promise<MattingResult>((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(requestId);
      reject(new Error('matting_timeout'));
    }, REQUEST_TIMEOUT_MS);
    pending.set(requestId, { resolve, reject, timer });
    w.postMessage({ type: 'infer', requestId, source: copy }, [copy]);
  });
}
